import { cn } from "@/lib/cn";
import { reais } from "@/lib/dinheiro";
import type { Linha } from "./use-importacao-de-notas";

const MOTIVO: Record<Exclude<Linha["situacao"], "pronta">, string> = {
  jaRegistrada: "Já registrada",
  repetida: "Repetida neste lote",
  emitida: "Emitida pelo laboratório (não é de fornecedor)",
  naoNota: "Não é uma nota fiscal",
  incompleta: "Sem número, data ou valor",
};
const RESULTADO = {
  importada: { texto: "Importada", tom: "text-emerald-700 dark:text-emerald-400" },
  mesFechado: { texto: "Mês fechado: só a nota", tom: "text-amber-700 dark:text-amber-400" },
  jaRegistrada: { texto: "Já registrada", tom: "text-zinc-500" },
  erro: { texto: "Erro", tom: "text-red-600 dark:text-red-400" },
  envio: { texto: "Arquivo não subiu", tom: "text-red-600 dark:text-red-400" },
} as const;

const data = (t: string | null) => (t ? t.split("-").reverse().join("/") : "—");
const select = "h-8 rounded-md border border-zinc-200 bg-white px-2 text-xs dark:border-border dark:bg-card";

/** Uma nota do lote: o que o XML trouxe, o fornecedor, a categoria e o que fazer com a despesa. */
export function LinhaDaImportacao({
  linha: l,
  categorias,
  categoriaId,
  bloqueada,
  onMudar,
}: {
  linha: Linha;
  categorias: { id: string; nome: string }[];
  categoriaId: string;
  bloqueada: boolean;
  onMudar: (parcial: Partial<Linha>) => void;
}) {
  const d = l.dados;
  const pronta = l.situacao === "pronta";
  const fim = l.resultado ? RESULTADO[l.resultado.resultado] : null;
  const travar = bloqueada || !!l.resultado;

  return (
    <li className={cn("flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-sm", !pronta && "opacity-60")}>
      <input
        type="checkbox"
        aria-label={`Importar ${l.arquivo.name}`}
        checked={l.incluir}
        disabled={!pronta || travar}
        onChange={(e) => onMudar({ incluir: e.target.checked })}
        className="h-4 w-4 rounded border-zinc-300 text-brand-600"
      />
      <div className="min-w-[200px] flex-1">
        <p className="truncate font-semibold text-zinc-900 dark:text-foreground">
          {d?.emitente.nome ?? l.arquivo.name}
          {pronta && !l.fornecedor && (
            <label className="ml-2 inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-[10.5px] font-bold uppercase text-brand-700 dark:bg-primary/10 dark:text-primary">
              <input type="checkbox" checked={l.criarFornecedor} disabled={travar} onChange={(e) => onMudar({ criarFornecedor: e.target.checked })} className="h-3 w-3" />
              novo fornecedor
            </label>
          )}
        </p>
        <p className="truncate text-xs text-zinc-500 dark:text-muted-foreground">
          {d?.numero ? `NF ${d.numero} · ${data(d.emitidaEm)}` : l.arquivo.name}
          {d?.emitente.documento && ` · ${d.emitente.documento}`}
        </p>
      </div>
      <span className="w-24 text-right font-semibold tabular-nums text-zinc-900 dark:text-foreground">{d?.valorCents ? reais(d.valorCents) : "—"}</span>

      {pronta ? (
        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Despesa" value={l.despesa} disabled={travar} onChange={(e) => onMudar({ despesa: e.target.value as Linha["despesa"] })} className={select}>
            <option value="nova">{l.mesFechado ? "Mês fechado: só a nota" : `Despesa paga em ${data(d?.emitidaEm ?? null)}`}</option>
            {l.despesaExistente && <option value="ligar">Ligar a: {l.despesaExistente.descricao}</option>}
            <option value="nenhuma">Só a nota</option>
          </select>
          {l.despesa === "nova" && !l.mesFechado && (
            <select aria-label="Categoria" value={categoriaId}disabled={travar} onChange={(e) => onMudar({ categoriaId: e.target.value })} className={select}>
              {categorias.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
          )}
        </div>
      ) : (
        <span className="text-xs text-zinc-500 dark:text-muted-foreground">{MOTIVO[l.situacao as keyof typeof MOTIVO]}</span>
      )}

      {fim && (
        <span className={cn("w-full text-xs font-semibold sm:w-auto", fim.tom)} title={l.resultado?.mensagem}>
          {fim.texto}
          {l.resultado?.mensagem && `: ${l.resultado.mensagem}`}
        </span>
      )}
    </li>
  );
}
